<template>
  <PageContent type="center" :compClass="'flex flex-col space-y-6'">
    <div class="flex flex-row justify-between items-center content-center">
      <h1 class="text-2xl font-medium">
        Users
      </h1>
      <button
        class="bg-blue-600 rounded-full text-white text-sm hover:bg-blue-500 focus:outline-none px-3 py-1.5 shadow-md flex flex-row items-center space-x-1"
        v-on:click="$eventBus.$emit('admin-add-user-open-modal')"
      >
        <i class="far fa-plus"></i> <span>Add User</span>
      </button>
    </div>

    <div
      class="bg-white rounded-lg shadow-md ring-1 ring-gray-600 ring-opacity-5"
    >
      <div class="flex flex-row p-4 justify-between items-center">
        <div class="mt-1 relative rounded-md shadow-sm">
          <div
            class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
          >
            <i class="fa fa-search text-gray-400 fa-fw"></i>
          </div>
          <input
            type="text"
            name="searchText"
            id="searchText"
            class="focus:ring-gray-500 focus:border-gray-500 pl-10 sm:text-sm border-gray-300 rounded-md w-96"
            placeholder="you@example.com"
            v-model="searchText"
          />
        </div>

        <div class="flex flex-row space-x-4">
          <button
            type="button"
            class="relative cursor-default rounded-md border border-gray-300 bg-white py-2 px-3 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm flex flex-row items-center content-center space-x-1"
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-labelledby="listbox-label"
            v-on:click="hideSuspendedUsers = !hideSuspendedUsers"
          >
            <input
              name="hideSuspendedUsers"
              id="hideSuspendedUsers"
              type="checkbox"
              class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded focus:outline-none mr-2"
              v-model="hideSuspendedUsers"
            />
            <label class="font-normal inline-block truncate">
              Hide Suspended Users
            </label>
          </button>
          <div class="relative">
            <button
              type="button"
              class="relative cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm w-60"
              aria-haspopup="listbox"
              aria-expanded="true"
              aria-labelledby="listbox-label"
              v-on:click="multiSelectExpand = !multiSelectExpand"
            >
              <span class="block truncate">{{
                filteringRoles.length > 0 ? "Multiple Roles Selected" : "A role"
              }}</span>
              <span
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
              >
                <!-- Heroicon name: mini/chevron-up-down -->
                <svg
                  class="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </button>

            <ul
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-2 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              tabindex="-1"
              role="listbox"
              aria-labelledby="listbox-label"
              aria-activedescendant="listbox-option-3"
              v-if="multiSelectExpand"
            >
              <li
                class="text-gray-900 relative cursor-default select-none p-2 flex flex-row items-center "
                id="listbox-option-0"
                role="option"
                v-for="role in roles"
                v-bind:key="role.type"
              >
                <input
                  :name="role.type"
                  :id="role.type"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded focus:outline-none mr-2"
                  v-model="role.selected"
                  v-on:change="updateSearch"
                />
                <label
                  :for="role.type"
                  class="font-normal inline-block truncate"
                  >{{ role.display }}</label
                >
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="min-w-full align-middle">
        <table
          class="min-w-full divide-y divide-gray-300 border-t-2 border-b-2 border-gray-300"
        >
          <thead class="bg-gray-50">
            <tr>
              <th
                scope="col"
                class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
              >
                <a href="#" class="group inline-flex">
                  Name
                  <!-- Active: "bg-gray-200 text-gray-900 group-hover:bg-gray-300", Not Active: "invisible text-gray-400 group-hover:visible group-focus:visible" -->
                  <span
                    class="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible"
                  >
                    <!-- Heroicon name: solid/chevron-down -->
                    <svg
                      class="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </a>
              </th>
              <th
                scope="col"
                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                <a href="#" class="group inline-flex">
                  Email
                </a>
              </th>
              <th
                scope="col"
                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                <a href="#" class="group inline-flex">
                  Role
                </a>
              </th>
              <th
                scope="col"
                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center"
              >
                <a href="#" class="group inline-flex">
                  W-9
                </a>
              </th>
              <th
                scope="col"
                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center"
                v-if="!hideSuspendedUsers"
              >
                <a href="#" class="group inline-flex">
                  Standing
                </a>
              </th>
              <th
                scope="col"
                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center"
              >
                <a href="#" class="group inline-flex">
                  Emails
                </a>
              </th>
              <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span class="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="user in users" v-bind:key="user.id">
              <td
                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
              >
                <router-link
                  :to="{
                    name: 'AdminUserInformation',
                    params: { uid: user.id }
                  }"
                >
                  {{ user.name }}
                </router-link>
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {{ user.email }}
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {{ user.displayRole }}
              </td>
              <td class="whitespace-nowrap text-gray-500 text-lg text-center">
                <i
                  class="fa-regular fa-circle-check text-green-500"
                  v-if="
                    !user.role.includes('media') && user.w9_timestamp !== null
                  "
                ></i>
                <i
                  class="fa-regular fa-circle-x text-red-500"
                  v-if="
                    !user.role.includes('media') && user.w9_timestamp == null
                  "
                ></i>
              </td>
              <td
                class="whitespace-nowrap text-gray-500 text-lg text-center"
                v-if="!hideSuspendedUsers"
              >
                <i
                  class="fa-regular fa-circle-check text-green-500"
                  v-if="!user.suspended"
                ></i>
                <i
                  class="fa-regular fa-circle-x text-red-500"
                  v-if="user.suspended"
                ></i>
              </td>
              <td class="whitespace-nowrap text-gray-500 text-center text-lg">
                <i
                  class="fa-regular fa-circle-check text-green-500"
                  v-if="!user.email_disabled && !user.email_manual_disabled"
                ></i>
                <i
                  class="fa-regular fa-circle-x text-red-500"
                  v-if="user.email_disabled || user.email_manual_disabled"
                ></i>
              </td>
              <td
                class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
              >
                <PopperDropdown>
                  <template v-slot:dropdown-button>
                    <button
                      type="button"
                      class="text-gray-400 hover:text-gray-900 focus:outline-none"
                      id="options-menu"
                      aria-haspopup="true"
                      aria-expanded="true"
                    >
                      <i class="fa-solid fa-ellipsis"></i>
                    </button>
                  </template>
                  <template v-slot:dropdown-content>
                    <div
                      class="px-4 py-1.5 text-xs bg-gray-100 text-gray-500"
                      role="none"
                    >
                      <p class="font-normal" role="none">
                        Quick Actions
                      </p>
                    </div>
                    <button
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 group hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                      role="menuitem"
                      v-on:click="
                        $eventBus.$emit('admin-user-update-modal-open', user)
                      "
                    >
                      Modify User Info
                    </button>
                    <button
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 group hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                      role="menuitem"
                      v-on:click="
                        $eventBus.$emit(
                          'admin-user-reset-password-modal-open',
                          user
                        )
                      "
                    >
                      Reset Password
                    </button>
                    <button
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 group hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                      role="menuitem"
                      v-on:click="
                        $eventBus.$emit(
                          'admin-user-change-permissions-modal-open',
                          user
                        )
                      "
                    >
                      Change Permissions
                    </button>
                    <button
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 group hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                      role="menuitem"
                      v-on:click="
                        $eventBus.$emit(
                          'admin-modify-stringer-percent-open-modal',
                          user
                        )
                      "
                      v-if="!user.role.includes('media')"
                    >
                      Modify User Percentage
                    </button>
                    <button
                      class="w-full flex items-center px-4 py-2 text-sm text-gray-700 group hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                      role="menuitem"
                      v-on:click="
                        $eventBus.$emit('admin-user-suspend-modal-open', user)
                      "
                    >
                      Suspend User
                    </button>
                  </template>
                </PopperDropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex flex-row justify-between items-center p-4">
        <span>
          Showing
          <strong class="font-medium">{{ paginationInfo.from }}</strong> to
          <strong class="font-medium">{{ paginationInfo.to }}</strong> of
          <strong class="font-medium">{{ paginationInfo.total }}</strong>
          results
        </span>
        <div class="flex flex-row items-center space-x-4">
          <select
            id="country"
            name="country"
            autocomplete="country-name"
            class="focus:ring-gray-500 focus:border-gray-500 shadow-sm text-sm border-gray-300 rounded-md -m-2"
            v-model="perPage"
          >
            <option v-for="amount in paginationSelector" v-bind:key="amount">{{
              amount
            }}</option>
          </select>
          <span>per page</span>
        </div>
        <div class="flex items-center justify-end">
          <div class="py-3 border rounded-lg dark:border-gray-600">
            <ol
              class="flex items-center text-sm text-gray-500 divide-x divide-gray-300 dark:text-gray-400"
            >
              <li
                v-for="(page, key) in pageRange"
                :key="key"
                v-on:click="goToPage(page)"
              >
                <button
                  type="button"
                  class="relative flex items-center justify-center font-medium min-w-[2rem] px-2 h-8 -my-3 rounded-md focus:outline-none filament-tables-pagination-item transition text-primary-600 focus:underline "
                  :class="{
                    'bg-gray-500 bg-opacity-20 ring-1 ring-gray-600':
                      page == paginationInfo.current_page,
                    'hover:bg-gray-400 hover:bg-opacity-20 cursor-pointer':
                      page !== '...',
                    'cursor-default': page == '...'
                  }"
                  :disabled="page == '...'"
                >
                  <span>{{ page }}</span>
                </button>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
    <UserAddModal />
    <UserResetPasswordModal />
    <UserChangePermissionsModal />
    <UserSuspendModal />
    <UserModifyPercentModal />
    <UserModifySettingsModal />
  </PageContent>
</template>

<script>
import * as Config from "@/config";

import PageContent from "@/components/PageContent";
import PopperDropdown from "@/components/partials/PopperDropdown";

import UserAddModal from "@/screens/admin/modals/UserAddModal";
import UserResetPasswordModal from "@/screens/admin/modals/UserResetPasswordModal";
import UserChangePermissionsModal from "@/screens/admin/modals/UserChangePermissionsModal";
import UserSuspendModal from "@/screens/admin/modals/UserSuspendModal";
import UserModifyPercentModal from "@/screens/admin/modals/UserModifyPercentModal";
import UserModifySettingsModal from "@/screens/admin/modals/UserModifySettingsModal";

export default {
  name: "AdminNetworks",
  data() {
    return {
      users: [],
      polling: false,
      perPage: 25,
      limit: 2,
      currentPage: 0,
      paginationInfo: {
        current_page: null,
        from: null,
        last_page: null,
        per_page: null,
        to: null,
        total: null
      },
      paginationSelector: [10, 25, 50],
      searchText: null,
      multiSelectExpand: false,
      roles: [
        {
          display: "Super Administrator",
          type: "superadministrator",
          selected: true
        },
        {
          display: "Administrator",
          type: "administrator",
          selected: true
        },
        {
          display: "Stringer",
          type: "videographer",
          selected: true
        },
        {
          display: "Media Producer",
          type: "media_producer",
          selected: true
        },
        {
          display: "Media Purchaser",
          type: "media_purchaser",
          selected: true
        }
      ],
      filteringRoles: [
        "superadministrator",
        "administrator",
        "videographer",
        "media_producer",
        "media_purchaser"
      ],
      hideSuspendedUsers: true
    };
  },
  components: {
    PageContent,
    UserAddModal,
    PopperDropdown,
    UserResetPasswordModal,
    UserChangePermissionsModal,
    UserSuspendModal,
    UserModifyPercentModal,
    UserModifySettingsModal
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    async getUsers() {
      this.polling = true;
      let urlPath =
        "/api/v2/admin/users/paginate?tableRecordsPerPage=" +
        this.perPage +
        "&page=" +
        this.currentPage;
      if (this.searchText !== null && this.searchText !== "") {
        urlPath += "&searchQuery=" + this.searchText;
      }
      if (this.filteringRoles !== null && this.filteringRoles.length > 0) {
        urlPath += "&userType=" + this.filteringRoles.join(",");
      }
      if (this.hideSuspendedUsers == true) {
        urlPath += "&hideSuspendedUsers=" + this.hideSuspendedUsers;
      }

      let res = await this.$http.get(Config.apiPath + urlPath);
      this.users = res.data.data;
      this.paginationInfo = {
        current_page: res.data.current_page,
        from: res.data.from,
        last_page: res.data.last_page,
        per_page: res.data.per_page,
        to: res.data.to,
        total: res.data.total
      };
      console.log(res.data);
    },
    goToPage(page) {
      if (page == "...") {
        return;
      }
      this.currentPage = page;
      this.getUsers();
    },
    updateSearch() {
      let filter = [];
      this.roles.forEach(role => {
        if (role.selected) {
          filter.push(role.type);
        }
      });
      this.filteringRoles = filter;
    }
  },
  watch: {
    searchText() {
      this.currentPage = 0;
      this.getUsers();
    },
    perPage() {
      this.currentPage = 0;
      this.getUsers();
    },
    filteringRoles() {
      this.currentPage = 0;
      this.getUsers();
    },
    hideSuspendedUsers() {
      this.currentPage = 0;
      this.getUsers();
    }
  },
  computed: {
    pageRange() {
      if (this.limit === -1) {
        return 0;
      }
      if (this.limit === 0) {
        return this.paginationInfo.last_page;
      }
      var current = this.paginationInfo.current_page;
      var last = this.paginationInfo.last_page;
      var delta = this.limit;
      var left = current - delta;
      var right = current + delta + 1;
      var range = [];
      var pages = [];
      var l;
      for (var i = 1; i <= last; i++) {
        if (i === 1 || i === last || (i >= left && i < right)) {
          range.push(i);
        }
      }
      range.forEach(function(i) {
        if (l) {
          if (i - l === 2) {
            pages.push(l + 1);
          } else if (i - l !== 1) {
            pages.push("...");
          }
        }
        pages.push(i);
        l = i;
      });
      return pages;
    }
  }
};
</script>
